'use client';

import Logo from '@/components/Logo';
import NotificationModal from '@/components/NotificationModal';
import AccountSettingModal from '@/components/AccountSettingModal';
import UserComponent from '@/components/User';
import { ROUTES } from '@/constaint/route';
import { PERMISSIONS } from '@/constaint/permissions';
import { useAuth } from '@/hooks/useAuth';
import { useCountNewNotifications } from '@/hooks/useNotification';
import { usePermissions } from '@/hooks/usePermissions';
import { logoutApi } from '@/services/api/auth';
import { getAvatar } from '@/utils/helper';
import {
  Avatar,
  Button,
  Chip,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownTrigger,
} from '@nextui-org/react';
import { ArrowRight, Bell, GearSix, List } from '@phosphor-icons/react';
import Image from 'next/image';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Menu, MenuItem, Sidebar } from 'react-pro-sidebar';
import { NAVIGATION } from './Sidebar.config';

const SidebarLayout = () => {
  const pathname = usePathname();
  const [collapsed, setCollapsed] = useState(false);
  const [isNotificationModalOpen, setIsNotificationModalOpen] = useState(false);
  const [isAccountSettingModalOpen, setIsAccountSettingModalOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const { session, logout } = useAuth();
  const { hasPermission } = usePermissions();
  const { data: countNewNotifications } = useCountNewNotifications();

  const toggleCollapse = useCallback(() => {
    setCollapsed((prevState) => !prevState);
  }, []);

  const handleLogout = async () => {
    await logout();
  };

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (isMobile && !collapsed) {
      setCollapsed(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, isMobile]);

  const isCollapsed = !collapsed || isMobile;

  const isActive = useCallback(
    (url: string | undefined) => {
      if (!url) return false;

      if (url === ROUTES.ADMIN.JOB) {
        return pathname.startsWith(ROUTES.ADMIN.JOB);
      }

      if (url === ROUTES.ADMIN.RECRUITER) {
        return pathname.startsWith(ROUTES.ADMIN.RECRUITER);
      }

      if (url === ROUTES.ADMIN.CANDIDATE) {
        return pathname.startsWith(ROUTES.ADMIN.CANDIDATE);
      }

      return pathname === url;
    },
    [pathname],
  );

  const menuItems = useMemo(() => {
    const filteredNavigation = NAVIGATION.filter((item) => {
      if (item.url === ROUTES.ADMIN.CLIENT) return hasPermission(PERMISSIONS.GET_CLIENT);
      if (item.url === ROUTES.ADMIN.JOB) return hasPermission(PERMISSIONS.GET_JOB);
      if (item.url === ROUTES.ADMIN.RECRUITER) return hasPermission(PERMISSIONS.GET_RECRUITER);
      if (item.url === ROUTES.ADMIN.CANDIDATE) return hasPermission(PERMISSIONS.GET_CANDIDATE);
      if (item.url === ROUTES.ADMIN.USER_SETTING) return hasPermission(PERMISSIONS.GET_ROLE);
      if (item.url === ROUTES.ADMIN.PAYMENT) return hasPermission(PERMISSIONS.GET_PAYMENT);
      if (item.url === ROUTES.ADMIN.APPLICATION) return hasPermission(PERMISSIONS.GET_APPLICATION);
      if (item.url === ROUTES.ADMIN.AI_ASSISTANT) return hasPermission(PERMISSIONS.SEARCH_RESUME);
      return true;
    });

    return filteredNavigation.map(({ title, url, icon, iconActive }, index: number) => (
      <MenuItem
        className='text-gray-400'
        key={index}
        component={<Link href={url} />}
        icon={isActive(url) ? iconActive : icon}
        active={isActive(url)}
        style={{
          padding: '10px',
        }}
      >
        {(!collapsed || !isMobile) && title}
      </MenuItem>
    ));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collapsed, isMobile, pathname, hasPermission]);

  return (
    <div>
      {isMobile && (
        <div className='flex items-center justify-between p-4 w-full'>
          <div>
            <Link href='/' className='cursor-pointer'>
              <Logo />
            </Link>
          </div>
          <div>
            <Button
              onClick={toggleCollapse}
              isIconOnly
              color='primary'
              variant='light'
              endContent={<List size={24} />}
            />
          </div>
        </div>
      )}
      <Sidebar
        width={isMobile ? '100%' : '280px'}
        collapsedWidth='88px'
        collapsed={collapsed && !isMobile}
        breakPoint='md'
        backgroundColor='#fff'
        style={{
          backgroundColor: 'white',
          overflow: 'hidden',
          height: '100vh',
          padding: '24px 16px',
          display: 'flex',
          justifyContent: 'space-between',
          transition: 'all 0.3s ease',
          // if collapsed, translateX -100% else translateX 0% but in mobile is translateX 100%
          transform: collapsed
            ? 'translateX(0%)'
            : isMobile
              ? 'translateX(100%)'
              : 'translateX(0%)',
        }}
      >
        <div className='w-full h-full flex flex-col justify-between'>
          <div>
            <div
              className='flex items-center justify-between mb-8 px-2 cursor-pointer'
              onClick={toggleCollapse}
            >
              <div className='flex items-center gap-2'>
                <div>
                  <div className='w-[40px] aspect-square flex items-center justify-center'>
                    <Logo />
                  </div>
                </div>
                {isCollapsed && <b className='text-lg font-bold'>KitesHR</b>}
              </div>
              {isCollapsed && (
                <div className='cursor-pointer relative -right-2'>
                  <Button
                    onClick={toggleCollapse}
                    isIconOnly
                    color='primary'
                    variant='light'
                    endContent={
                      <Image
                        src='/assets/icons/left-bar.svg'
                        alt='left-bar'
                        width={24}
                        height={24}
                      />
                    }
                  />
                </div>
              )}
            </div>
            <Menu
              menuItemStyles={{
                button: ({ active }) => ({
                  backgroundColor: active ? '#F9FAFB' : '#fff',
                  color: active ? 'var(--brand-color)' : 'var(--brand-gray-color)',
                  fontWeight: active ? 600 : 500,
                  borderRadius: '8px',
                  width: collapsed && isMobile ? '56px' : '100%',
                }),
                icon: ({ active }) => ({
                  color: active ? 'var(--brand-color)' : 'var(--brand-gray-color) !important',
                }),
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                overflow: 'visible',
              }}
            >
              <MenuItem
                className='text-gray-400'
                style={{
                  padding: '10px',
                }}
                icon={<Bell size={24} />}
                onClick={() => setIsNotificationModalOpen(true)}
              >
                <div className='flex items-center gap-2 justify-between w-full'>
                  <span>Notifications</span>
                  <Chip
                    color='secondary'
                    className='bg-secondary/10 text-secondary rounded-sm'
                    size='sm'
                    variant='flat'
                  >
                    {countNewNotifications ?? 0}
                  </Chip>
                </div>
              </MenuItem>
              {menuItems}
            </Menu>
          </div>
          <div className='p-[10px]'>
            {collapsed && (
              <div className='my-4'>
                <Button
                  variant='light'
                  onClick={toggleCollapse}
                  isIconOnly
                  startContent={
                    <ArrowRight
                      size={24}
                      weight='bold'
                      className='text-[var(--brand-gray-color)]'
                    />
                  }
                />
              </div>
            )}
            {collapsed ? (
              <Dropdown placement='bottom-end'>
                <DropdownTrigger>
                  <Avatar
                    className='transition-transform'
                    src={session?.user?.avatar ?? getAvatar(session?.user?.id)}
                  />
                </DropdownTrigger>
                <DropdownMenu aria-label='Profile Actions' variant='flat'>
                  <DropdownItem key='profile' className='h-14 gap-2'>
                    <UserComponent />
                  </DropdownItem>
                  <DropdownItem
                    key='account-setting'
                    onClick={() => setIsAccountSettingModalOpen(true)}
                    startContent={
                      <GearSix size={24} />
                    }
                  >
                    Account Setting
                  </DropdownItem>
                  <DropdownItem
                    key='logout'
                    onClick={handleLogout}
                    startContent={
                      <Image src='/assets/icons/logout.svg' alt='logout' width={24} height={24} />
                    }
                  >
                    Log Out
                  </DropdownItem>
                </DropdownMenu>
              </Dropdown>
            ) : (
              <div className='flex items-center justify-between gap-2'>
                <UserComponent isShowToggle />
              </div>
            )}
          </div>
        </div>
      </Sidebar>
      {isNotificationModalOpen && (
        <NotificationModal
          isOpen={isNotificationModalOpen}
          onClose={() => setIsNotificationModalOpen(false)}
        />
      )}
      {isAccountSettingModalOpen && (
        <AccountSettingModal
          isOpen={isAccountSettingModalOpen}
          onClose={() => setIsAccountSettingModalOpen(false)}
        />
      )}
    </div>
  );
};

export default SidebarLayout;
